import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { OfflineProjektStatus, ProjektOfflineZustand } from '../../../Models/offline.model';
import { ProjektOfflineService } from '../../../Services/offline/projekt-offline.service';
import { OfflineSyncService } from '../../../Services/offline/offline-sync.service';
import {
  SyncKonfliktDialogComponent,
  SyncKonfliktDialogData,
  SyncKonfliktDialogErgebnis
} from '../sync-konflikt-dialog/sync-konflikt-dialog.component';

/** Anzeige eines Zustands: Symbol, Beschriftung, Farbklasse. */
interface ZustandAnzeige {
  icon: string;
  text: string;
  klasse: string;
}

/**
 * Der Offline-Block eines Projekts in der Projektansicht.
 *
 * Hier wird das GANZE Projekt heruntergeladen, aktualisiert oder vom Gerät
 * entfernt – mit sichtbarem Fortschritt und genau einem von sieben
 * Zuständen: nicht heruntergeladen, lädt, verfügbar, ausstehende
 * Änderungen, wird synchronisiert, Konflikte, Fehler. Dazu der Stand der
 * Daten und die letzte erfolgreiche Synchronisierung.
 *
 * Die Knöpfe bieten nur an, was gerade geht: ohne Verbindung kein Download,
 * mit offenen Änderungen kein Entfernen.
 */
@Component({
  selector: 'app-offline-projekt',
  templateUrl: './offline-projekt.component.html',
  styleUrls: ['./offline-projekt.component.css']
})
export class OfflineProjektComponent implements OnInit, OnDestroy {

  @Input({ required: true }) projektId!: number;

  @Input() projektName = '';

  /**
   * Arbeitet die aufrufende Seite gerade aus dem lokalen Bestand?
   * Dann gibt es keinen Download – der braucht zwingend eine Verbindung.
   */
  @Input() seiteIstOffline = false;

  /** Meldet, dass sich am lokalen Bestand etwas geändert hat (Download, Entfernen). */
  @Output() geaendert = new EventEmitter<void>();

  status: OfflineProjektStatus | null = null;

  /** Eine Aktion läuft (Sync anstoßen, Entfernen) – gegen Doppelklicks. */
  beschaeftigt = false;

  private abo?: Subscription;

  constructor(
    private offline: ProjektOfflineService,
    private sync: OfflineSyncService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.abo = this.offline.statusFuer$(this.projektId).subscribe(s => (this.status = s));
    void this.offline.aktualisiereStatus();
  }

  ngOnDestroy(): void {
    this.abo?.unsubscribe();
  }

  // ===================================================================
  // ANZEIGE
  // ===================================================================

  get zustand(): ProjektOfflineZustand {
    return this.status?.zustand ?? 'nicht_heruntergeladen';
  }

  get verfuegbar(): boolean {
    return !!this.status?.verfuegbar;
  }

  get laedt(): boolean {
    return this.zustand === 'laedt';
  }

  /** Ausstehende Änderungen plus offene Konflikte plus abgelehnte – alles hält vom Entfernen ab. */
  get offen(): number {
    return (this.status?.ausstehend ?? 0) + (this.status?.konflikte ?? 0) + (this.status?.fehlgeschlagen ?? 0);
  }

  get fortschrittProzent(): number {
    const f = this.status?.fortschritt;

    if (!f || f.gesamt === 0) {
      return 0;
    }

    return Math.round(100 * f.erledigt / f.gesamt);
  }

  get anzeige(): ZustandAnzeige {
    const s = this.status;

    switch (this.zustand) {
      case 'laedt':
        return { icon: 'cloud_download', text: 'Wird heruntergeladen …', klasse: 'zustand-laedt' };

      case 'verfuegbar':
        return { icon: 'offline_pin', text: 'Vollständig offline verfügbar', klasse: 'zustand-verfuegbar' };

      case 'ausstehend':
        return {
          icon: 'cloud_queue',
          text: s?.ausstehend === 1
            ? '1 Änderung noch nicht synchronisiert'
            : `${s?.ausstehend ?? 0} Änderungen noch nicht synchronisiert`,
          klasse: 'zustand-ausstehend'
        };

      case 'synchronisiert':
        return { icon: 'sync', text: 'Wird synchronisiert …', klasse: 'zustand-synchronisiert' };

      case 'konflikt':
        return {
          icon: 'merge_type',
          text: s?.konflikte === 1
            ? '1 Konflikt braucht eine Entscheidung'
            : `${s?.konflikte ?? 0} Konflikte brauchen eine Entscheidung`,
          klasse: 'zustand-konflikt'
        };

      case 'fehler':
        return {
          icon: 'sync_problem',
          text: this.verfuegbar ? 'Synchronisierungsfehler' : 'Download fehlgeschlagen',
          klasse: 'zustand-fehler'
        };

      default:
        return { icon: 'cloud_off', text: 'Nicht heruntergeladen', klasse: 'zustand-nicht' };
    }
  }

  get tooltip(): string {
    const teile: string[] = [this.anzeige.text];

    if (this.status?.fehlertext) {
      teile.push(this.status.fehlertext);
    }

    if (this.status?.schemaVeraltet) {
      teile.push('Die Offline-Daten stammen aus einer älteren Programmversion – bitte aktualisieren.');
    }

    return teile.join('\n');
  }

  // ===================================================================
  // AKTIONEN
  // ===================================================================

  /** Lädt das ganze Projekt – oder aktualisiert einen vorhandenen Bestand. */
  async herunterladen(event: Event): Promise<void> {
    event.stopPropagation();

    if (this.laedt) {
      return;
    }

    const ergebnis = await this.offline.herunterladen(this.projektId);

    if (ergebnis.ok) {
      this.melde(true, this.verfuegbar
        ? 'Offline-Daten aktualisiert.'
        : `„${this.projektName || 'Projekt'}" ist jetzt vollständig offline verfügbar.`);
      this.geaendert.emit();
      return;
    }

    if (ergebnis.grund === 'konflikt') {
      this.melde(false, ergebnis.meldung);
      this.zeigeKonflikte();
      return;
    }

    this.melde(false, ergebnis.meldung);
  }

  /** Stößt die Synchronisierung an – nach dem Ergebnis folgt gegebenenfalls der Konfliktdialog. */
  async synchronisieren(event: Event): Promise<void> {
    event.stopPropagation();

    if (this.beschaeftigt) {
      return;
    }

    this.beschaeftigt = true;

    try {
      const status = await this.sync.synchronisiere('manuell');

      if (status.konflikte > 0 || status.fehlgeschlagen > 0) {
        this.zeigeKonflikte();
        return;
      }

      this.melde(true, status.ausstehend === 0 ? 'Alles synchronisiert.' : status.meldung);
    } finally {
      this.beschaeftigt = false;
    }
  }

  konflikte(event: Event): void {
    event.stopPropagation();
    this.zeigeKonflikte();
  }

  /** Entfernt den lokalen Bestand – aber nur, wenn nichts mehr aussteht. */
  async entfernen(event: Event): Promise<void> {
    event.stopPropagation();

    if (this.beschaeftigt || this.laedt) {
      return;
    }

    if (this.offen > 0) {
      this.melde(false, 'Es gibt noch nicht synchronisierte Änderungen. Bitte zuerst synchronisieren.');
      return;
    }

    if (!confirm(`Offline-Daten von „${this.projektName || 'diesem Projekt'}" von diesem Gerät entfernen?`)) {
      return;
    }

    this.beschaeftigt = true;

    try {
      const ergebnis = await this.offline.entferne(this.projektId);
      this.melde(ergebnis.ok, ergebnis.ok ? 'Offline-Daten entfernt.' : ergebnis.meldung);

      if (ergebnis.ok) {
        this.geaendert.emit();
      }
    } finally {
      this.beschaeftigt = false;
    }
  }

  private zeigeKonflikte(): void {
    this.dialog
      .open<SyncKonfliktDialogComponent, SyncKonfliktDialogData, SyncKonfliktDialogErgebnis | undefined>(
        SyncKonfliktDialogComponent,
        {
          width: '720px',
          maxWidth: '96vw',
          autoFocus: false,
          data: { projektId: this.projektId }
        })
      .afterClosed()
      .subscribe(ergebnis => {
        void this.offline.aktualisiereStatus();

        // „Anderen QR-Code vergeben": direkt zur Messstelle, der Scanner ist dort.
        if (ergebnis?.navigiereZuMessstelle != null) {
          void this.router.navigate(['/wartung/inbetriebnahme/messstelle', ergebnis.navigiereZuMessstelle]);
        }
      });
  }

  private melde(ok: boolean, text: string): void {
    this.snackBar.open(text, 'OK', { duration: ok ? 3500 : 7000 });
  }
}
